import { useEffect, useRef, useState } from "react";

export interface CascadeNode<T> {
  label: string;
  value: T;
  children?: CascadeNode<T>[];
}

export interface CascadeOption<T> {
  label: string;
  value: T;
}

interface CascadeFilterProps<T> {
  tree: CascadeNode<T>[];
  value?: CascadeOption<T> | null;
  onChange: (value: CascadeOption<T> | null) => void;
  isSameValue: (a: T, b: T) => boolean;
  getNodeKey?: (node: CascadeNode<T>) => string;
  placeholder?: string;
  title?: string;
  clearLabel?: string;
  triggerTestId?: string;
  panelTestId?: string;
  clearTestId?: string;
  className?: string;
}

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const findPath = <T,>(
  target: T,
  isSameValue: (a: T, b: T) => boolean,
  nodes: CascadeNode<T>[],
  trail: CascadeNode<T>[] = [],
): CascadeNode<T>[] => {
  for (const node of nodes) {
    const nextTrail = [...trail, node];
    if (isSameValue(node.value, target)) return nextTrail;
    if (node.children) {
      const found = findPath(target, isSameValue, node.children, nextTrail);
      if (found.length) return found;
    }
  }
  return [];
};

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

const ChevronDown = () => (
  <svg {...iconProps} className="size-4 shrink-0">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg {...iconProps} className="size-3.5 shrink-0">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg {...iconProps} className="size-3.5">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const CascadeFilter = <T,>({
  tree,
  value,
  onChange,
  isSameValue,
  getNodeKey,
  placeholder = "Selecione",
  title,
  clearLabel = "Limpar",
  triggerTestId,
  panelTestId,
  clearTestId,
  className,
}: CascadeFilterProps<T>) => {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<CascadeNode<T>[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const columns: CascadeNode<T>[][] = [tree];
  path.forEach((node) => {
    if (node.children?.length) columns.push(node.children);
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    setPath(next && value ? findPath(value.value, isSameValue, tree) : []);
  };

  const handleHover = (level: number, node: CascadeNode<T>) => {
    setPath((prev) => [...prev.slice(0, level), node]);
  };

  const handleSelect = (level: number, node: CascadeNode<T>) => {
    const selectedPath = [...path.slice(0, level), node];
    onChange({ label: selectedPath.map((node) => node.label).join(" › "), value: node.value });
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid={triggerTestId}
        data-state={open ? "open" : "closed"}
        onClick={toggle}
        className={cx(
          "flex items-center gap-2 h-8 px-3 rounded-xl text-xs whitespace-nowrap bg-neutral-op-8 text-white-800",
          "shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)] data-[state=open]:ring-1 data-[state=open]:ring-blue-700",
          className,
        )}
      >
        {value && <span className="size-1.5 shrink-0 rounded-full bg-blue-700" />}
        <span className={cx("max-w-60 truncate", !value && "italic text-white-800/70")}>
          {value?.label || placeholder}
        </span>
        <ChevronDown />
      </button>

      {open && (
        <div
          data-testid={panelTestId}
          className="absolute left-0 top-full z-50 mt-1.5 w-auto rounded-md overflow-hidden bg-[#383838] text-white-800 shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]"
        >
          <div className="flex items-center justify-between gap-4 px-3 py-2 border-b border-black-300">
            <p className="text-xs font-light italic">{title ?? placeholder}</p>
            {value && (
              <button
                type="button"
                data-testid={clearTestId}
                onClick={handleClear}
                className="flex items-center gap-1 text-xs text-white-800/70 hover:text-white-800"
              >
                <CloseIcon />
                {clearLabel}
              </button>
            )}
          </div>

          <div className="flex">
            {columns.map((column, level) => (
              <ul
                key={level}
                className="min-w-[200px] max-h-[260px] overflow-y-auto no-scrollbar border-r border-black-300 last:border-r-0 py-1"
              >
                {column.map((node, index) => {
                  const isActive = path[level]?.label === node.label;
                  const isSelected = !!value && isSameValue(value.value, node.value);
                  const hasChildren = !!node.children?.length;

                  return (
                    <li
                      key={getNodeKey ? getNodeKey(node) : `${level}-${index}`}
                      onMouseEnter={() => handleHover(level, node)}
                      onClick={() => handleSelect(level, node)}
                      className={cx(
                        "flex items-center justify-between gap-3 px-3 py-2 text-xs cursor-pointer",
                        "hover:bg-[#444444]",
                        isActive && "bg-[#444444]",
                        isSelected && "bg-blue-700 hover:bg-blue-700",
                      )}
                    >
                      <span className="truncate">{node.label}</span>
                      {hasChildren && <ChevronRight />}
                    </li>
                  );
                })}
              </ul>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CascadeFilter;
